import { useState, useEffect, memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Menu, X, ArrowRight } from "lucide-react";
import { Link as ScrollLink, scroller } from "react-scroll";

const navLinks = [
  { id: 1, title: "Home", to: "hero" },
  { id: 2, title: "Tutors", to: "our-tutors" },
  { id: 3, title: "Courses", to: "services" },
  { id: 4, title: "About", to: "about-us" },
  { id: 5, title: "FAQ", to: "faq" },
  { id: 6, title: "Contact", to: "contact-us" },
];

const announcements = [
  "Free Career Counselling & Academic Guidance — Now Live",
  "Mathematics & Computer Science — Classes 1–12 (All Boards) | Live",
  "International Mathematics Curriculum — IB, IGCSE, A-Level, AP | Live",
  "Engineering Mathematics & Programming Support — Live",
  "Programming Languages — Python, Java, C/C++ | Live",
];

export default memo(function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isOpen]);

  return (
    <>
      {/* Announcement Bar */}
      <div className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="py-2 overflow-hidden">
          <motion.div
            className="flex gap-4 sm:gap-8 lg:gap-12 whitespace-nowrap"
            animate={{ x: ["0%", "-50%"] }}
            transition={{
              x: {
                repeat: Infinity,
                repeatType: "loop",
                duration: window.innerWidth < 640 ? 10 : window.innerWidth < 1024 ? 12 : 15, 
                ease: "linear",
              },
            }}
          >
            {[...announcements, ...announcements].map((msg, idx) => (
              <span
                key={idx}
                className="flex items-center text-xs sm:text-sm font-medium text-slate-200 flex-shrink-0"
              >
                <Sparkles className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 text-amber-400" />
                {msg}
              </span>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Main Navbar */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed left-0 right-0 z-[90] transition-all duration-500 ${
          scrolled
            ? "top-8 bg-white shadow-lg shadow-slate-200/50 border-b border-slate-200"
            : "top-8 bg-white border-b border-slate-200/50"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <ScrollLink
              to="hero"
              smooth
              duration={500}
              offset={-100}
              className="flex items-center gap-3 cursor-pointer"
            >
              {/* Logo Image */}
              <div className="relative">
                <div className="relative w-11 h-11 lg:w-13 lg:h-13">
                  {/* Square Border */}
                  <div className="absolute inset-0 border-2 border-blue-500 rounded-lg shadow-lg shadow-blue-500/20"></div>
                  
                  {/* Rotating Logo Inside Square Border */}
                  <motion.div
                    className="absolute inset-1 overflow-hidden rounded"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                  >
                    <img 
                      src="/globalmath.svg" 
                      alt="Global Math Tutoring Network"
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                </div>
              </div>
              
              {/* Brand Name */}
              <div>
                <h2 className="font-bold text-base sm:text-lg lg:text-xl leading-tight">
                  <span className="bg-gradient-to-r from-slate-900 via-blue-600 to-violet-600 bg-clip-text text-transparent">
                    Global Math Tutoring Network
                  </span>
                </h2>
              </div>
            </ScrollLink>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => {
                    scroller.scrollTo(link.to, {
                      smooth: true,
                      duration: 500,
                      offset: -100,
                    });
                  }}
                  className="relative px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors duration-200 group cursor-pointer bg-transparent border-none"
                >
                  <span className="flex items-center gap-1.5">
                    {link.title}
                    {link.badge && (
                      <span className="px-1.5 py-0.5 text-[10px] font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 rounded-full">
                        {link.badge}
                      </span>
                    )}
                  </span>
                  <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-gradient-to-r from-blue-600 to-violet-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-full" />
                </button>
              ))}
            </div>

            {/* CTA Button - Desktop */}
            <div className="hidden lg:block">
              <motion.button
                onClick={() => {
                  scroller.scrollTo('contact-us', {
                    smooth: true,
                    duration: 500,
                    offset: -100,
                  });
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="group relative px-6 py-2.5 bg-slate-900 text-white font-semibold text-sm rounded-full overflow-hidden shadow-lg shadow-slate-900/20 hover:shadow-xl hover:shadow-slate-900/30 transition-all duration-300"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-blue-600 to-violet-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="relative flex items-center gap-2">
                  Get Started
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </motion.button>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              whileTap={{ scale: 0.95 }}
              className="lg:hidden relative w-11 h-11 flex items-center justify-center rounded-xl bg-white border border-slate-200 shadow-sm text-slate-700 hover:text-slate-900 hover:border-slate-300 transition-all duration-200"
              aria-label="Toggle menu"
            >
              <AnimatePresence mode="wait">
                {isOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="w-5 h-5" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="w-5 h-5" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-30 lg:hidden"
          >
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
              onClick={() => setIsOpen(false)}
            />

            {/* Menu Panel */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="absolute right-0 top-0 bottom-0 w-[90vw] max-w-md bg-white shadow-2xl"
            >
              <div className="flex flex-col h-full pt-28 pb-8 px-8">
                {/* Navigation Links */}
                <nav className="flex-1">
                  <ul className="space-y-1">
                    {navLinks.map((link, index) => (
                      <motion.li
                        key={link.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <button
                          onClick={() => {
                            scroller.scrollTo(link.to, {
                              smooth: true,
                              duration: 500,
                              offset: -100,
                            });
                            setIsOpen(false);
                          }}
                          className="w-full flex items-center justify-between px-5 py-5 text-lg font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded-xl transition-all duration-200 cursor-pointer group text-left bg-transparent border-none"
                        >
                          <span className="flex items-center gap-3">
                            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-sm font-bold text-slate-500 group-hover:from-blue-100 group-hover:to-violet-100 group-hover:text-blue-600 transition-all">
                              {link.title.charAt(0)}
                            </span>
                            {link.title}
                          </span>
                          {link.badge && (
                            <span className="px-2 py-1 text-xs font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 rounded-full">
                              {link.badge}
                            </span>
                          )}
                        </button>
                      </motion.li>
                    ))}
                  </ul>
                </nav>

                {/* CTA Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="pt-6 border-t border-slate-100"
                >
                  <button
                    onClick={() => {
                      scroller.scrollTo('contact-us', {
                        smooth: true,
                        duration: 500,
                        offset: -100,
                      });
                      setIsOpen(false);
                    }}
                    className="w-full py-5 bg-gradient-to-r from-slate-900 to-slate-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    Get Started Today
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  <p className="text-center text-sm text-slate-500 mt-4">
                    Free career guidance available
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
});
